import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import NRHighchartTooltip from './NRHighchartTooltip/NRHighchartTooltip';
import ReactDOMServer from 'react-dom/server';
import { CONSTANTS } from '../../constants/constants';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';

const INNER_DONUT_MIN_WIDTH_PERCENTAGE = 1;
const OUTER_DONUT_MIN_WIDTH_2_PERCENT = 2;
const OUTER_DONUT_MIN_WIDTH_20_PERCENT = 20;
const OUTER_DONUT_MIN_WIDTH_50_PERCENT = 50;
const useStyles = makeStyles(theme => ({
  mainChart: {
    '& .donut-value': {
      ...theme.typography.h2
    },
    position: 'relative'
  }
}));

function NRCryptographyChart2({
  certs = 0,
  invalidCerts = 0,
  publicKeys = 0,
  publicFoundInPrivate = 0,
  privateKeys = 0,
  privateFoundInPublic = 0,
  handleOnClick,
  /** When true, disables Highcharts series animation (better for print / react-to-print snapshots). */
  disableAnimation = false
}) {
  const theme = useTheme();
  const classes = useStyles();
  const centerTitleRef = useRef(null);
  const centerTitleRafIdRef = useRef(null);
  const [isCenterTitleReady, setIsCenterTitleReady] = useState(false);

  useEffect(() => {
    return () => {
      if (centerTitleRafIdRef.current !== null) {
        cancelAnimationFrame(centerTitleRafIdRef.current);
      }
    };
  }, []);

  const { options, totalCryptography } = useMemo(() => {
    const {
      CERTIFICATES,
      PUBLIC_KEY,
      PRIVATE_KEY,
      CERTIFICATES_INVALID,
      CERTIFICATES_VALID,
      PUBLIC_KEY_INVALID,
      PUBLIC_KEY_VALID,
      PRIVATE_KEY_INVALID,
      PRIVATE_KEY_VALID
    } = CONSTANTS.CRYPTOGRAPHY;

    const BORDERWIDTH_CHART_1 = 1;
    const BORDER_COLOR = theme.palette.background.paper;
    const OFFSET_ANGLE_CHART_2 = 0;
    const reducer = (previousValue, currentValue) => previousValue + currentValue;

    const VALUES_WHITE = [
      {
        name: CERTIFICATES,
        realValue: certs,
        y: calculatePercentage(certs, [certs, publicKeys, privateKeys]),
        events: { click: e => handleOnClick?.(e) },
        labelrank: 1
      },
      {
        name: PUBLIC_KEY,
        realValue: publicKeys,
        y: calculatePercentage(publicKeys, [certs, publicKeys, privateKeys]),
        events: { click: e => handleOnClick?.(e) }
      },
      {
        name: PRIVATE_KEY,
        realValue: privateKeys,
        y: calculatePercentage(privateKeys, [certs, publicKeys, privateKeys]),
        events: { click: e => handleOnClick?.(e) }
      }
    ];

    const VALUES = [invalidCerts, publicFoundInPrivate, privateFoundInPublic];
    const CATEGORIES = [CERTIFICATES_INVALID, PUBLIC_KEY_INVALID, PRIVATE_KEY_INVALID];
    const VALID_CATEGORIES = [CERTIFICATES_VALID, PUBLIC_KEY_VALID, PRIVATE_KEY_VALID];

    const totalCryptographyLocal = VALUES_WHITE.map(e => e.realValue).reduce(reducer, 0);

    const arrayLength = VALUES.length;
    const COLOR_VALUES = [];
    const begin = [];
    const end = [];

    for (let i = 0; i < arrayLength; i++) {
      const percInvalid = VALUES_WHITE[i].realValue === 0 ? 0 : VALUES[i] / VALUES_WHITE[i].realValue;

      let percTotalPrev =
        i !== 0
          ? VALUES_WHITE.slice(0, i)
              .map(e => e.realValue)
              .reduce(reducer, 0) / (totalCryptographyLocal || 1)
          : 0;

      begin[i] = Math.floor(OFFSET_ANGLE_CHART_2 + 360 * percTotalPrev);
      end[i] =
        Math.floor(
          360 *
            (VALUES_WHITE.slice(0, i + 1)
              .map(e => e.realValue)
              .reduce(reducer, 0) /
              (totalCryptographyLocal || 1))
        ) - OFFSET_ANGLE_CHART_2;

      const label = CATEGORIES[i] + ': ' + VALUES[i];
      const valueInvalid =
        percInvalid === 0 || VALUES_WHITE[i].realValue === 0
          ? 0
          : percInvalid === 1
          ? 100
          : end[i] - begin[i] <= 7.2
          ? OUTER_DONUT_MIN_WIDTH_50_PERCENT
          : end[i] - begin[i] <= 36
          ? applyMinWidth(percInvalid, OUTER_DONUT_MIN_WIDTH_20_PERCENT)
          : applyMinWidth(percInvalid, OUTER_DONUT_MIN_WIDTH_2_PERCENT);

      COLOR_VALUES.push([
        {
          name: label,
          y: valueInvalid,
          category: CATEGORIES[i],
          realValue: VALUES[i],
          events: { click: e => handleOnClick?.(e) }
        },
        {
          name: VALID_CATEGORIES[i] + ': ' + (VALUES_WHITE[i].realValue - VALUES[i]),
          y: VALUES_WHITE[i].realValue === 0 ? 0 : 100 - valueInvalid,
          category: VALID_CATEGORIES[i],
          realValue: VALUES_WHITE[i].realValue - VALUES[i],
          events: { click: e => handleOnClick?.(e) }
        }
      ]);
    }

    adjustSections(begin, end);

    const backgroundSeries = {
      type: 'pie',
      name: 'background',
      data: VALUES_WHITE,
      showInLegend: true,
      enableMouseTracking: true,
      borderColor: BORDER_COLOR,
      borderWidth: BORDERWIDTH_CHART_1,
      innerSize: '60%',
      size: '80%',
      point: {
        events: {
          mouseOver: function () {
            this.graphic.toFront();
          }
        }
      },
      states: {
        hover: { brightness: 0, borderColor: BORDER_COLOR, halo: false }
      },
      colors: theme.palette.customPalette,
      dataLabels: false,
      center: ['45%', '50%'],
      color: theme.palette.divider
    };

    const mkForeground = idx => ({
      type: 'pie',
      name: 'foreground',
      cumulative: 1,
      data: COLOR_VALUES[idx],
      states: { hover: { brightness: 0, borderColor: BORDER_COLOR, halo: false } },
      borderColor: BORDER_COLOR,
      borderWidth: BORDERWIDTH_CHART_1,
      innerSize: '90%',
      size: '88%',
      colors: theme.palette.statusPalette.slice(0, 2).reverse(),
      dataLabels: false,
      startAngle: begin[idx],
      endAngle: end[idx],
      center: ['45%', '50%'],
      color: theme.palette.divider
    });

    const seriesArr = [backgroundSeries, mkForeground(0), mkForeground(1), mkForeground(2)];

    if (!!handleOnClick) {
      seriesArr.forEach(s => (s.cursor = 'pointer'));
    }

    const positionCenterTitle = function (chart) {
      const el = centerTitleRef.current;
      if (!el) return;
      const s = chart.series && chart.series[0];
      if (!s || !s.center) return;
      const left = chart.plotLeft + s.center[0];
      const top = chart.plotTop + s.center[1];
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      setIsCenterTitleReady(prev => (prev ? prev : true));
    };

    const schedulePositionCenterTitle = chart => {
      if (centerTitleRafIdRef.current !== null) {
        cancelAnimationFrame(centerTitleRafIdRef.current);
      }
      centerTitleRafIdRef.current = requestAnimationFrame(() => {
        centerTitleRafIdRef.current = null;
        positionCenterTitle(chart);
      });
    };

    const optionsMemo = {
      chart: {
        type: 'pie',
        height: 240,
        width: 400,
        backgroundColor: theme.palette.background.paper,
        spacingRight: 8,
        reflow: false,
        animation: !disableAnimation,
        events: {
          load: function () {
            schedulePositionCenterTitle(this);
          },
          render: function () {
            schedulePositionCenterTitle(this);
          }
        }
      },
      accessibility: {
        description:
          'A donut chart showing the composition of total cryptography assets, divided into Certificates, Public Keys, and Private Keys, and further segmented by invalid and valid counts within each type.',
        keyboardNavigation: { enabled: true },
        series: {
          descriptionFormatter: function (series) {
            const isBackgroundSeries = series.name === 'background';
            const total = isBackgroundSeries ? totalCryptographyLocal : '';
            return `${series.name} series. ${isBackgroundSeries ? 'Total assets: ' + total + '.' : ''}`;
          }
        },
        point: {
          descriptionFormatter: function (point) {
            const realValue = point.options.realValue !== undefined ? point.options.realValue : point.y;
            const category = point.options.category ? `(Category: ${point.options.category})` : '';
            return `${point.name} value is ${realValue}. ${category}`;
          }
        }
      },
      plotOptions: {
        series: { animation: !disableAnimation },
        pie: {
          animation: !disableAnimation,
          point: {
            events: {
              legendItemClick: e => {
                setTimeout(() => handleOnClick?.(e), 0);
                return false;
              }
            }
          }
        }
      },
      title: { text: null },
      legend: {
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'middle',
        floating: false,
        x: -6,
        itemMarginTop: 8,
        itemHoverStyle: { color: theme.palette.text.primary },
        itemStyle: { color: theme.palette.text.primary, ...theme.typography.body2 },
        labelFormatter: function () {
          return this.name + ' - ' + (this.options.realValue || this.options.y);
        },
        title: {
          text: 'Types',
          style: {
            color: theme.palette.text.secondary,
            ...theme.typography.label,
            marginBottom: '10px',
            marginLeft: '10px'
          }
        }
      },
      tooltip: {
        formatter: function () {
          let content = this.point.name;
          switch (this.point.name) {
            case CERTIFICATES:
            case PUBLIC_KEY:
            case PRIVATE_KEY:
              content += ': ' + (this.point.options.realValue || this.point.options.y);
              break;
          }
          const element = (
            <NRHighchartTooltip
              label={content.split(':')[0].trim()}
              value={content.split(':')[1]?.trim()}
              color={this.point.color}
              muiTheme={theme}
            />
          );
          return ReactDOMServer.renderToStaticMarkup(element);
        },
        outside: true,
        backgroundColor: theme.palette.tooltip.background,
        borderRadius: 10,
        borderWidth: 0,
        useHTML: true,
        padding: 0,
        shadow: false,
        style: { color: theme.palette.tooltip.color }
      },
      credits: { enabled: false },
      series: seriesArr,
      responsive: {
        rules: [
          {
            condition: { maxWidth: 360 },
            chartOptions: {
              // Repeat `chart.animation` here so a responsive rule firing during
              // a react-to-print snapshot can't re-enable animation mid-capture.
              chart: { animation: !disableAnimation },
              legend: { align: 'center', verticalAlign: 'bottom', layout: 'vertical', x: 0 },
              series: [
                { center: ['50%', '50%'], size: '80%' },
                { center: ['50%', '50%'], size: '88%' },
                { center: ['50%', '50%'], size: '88%' },
                { center: ['50%', '50%'], size: '88%' }
              ]
            }
          },
          {
            condition: { minWidth: 361 },
            chartOptions: {
              chart: { animation: !disableAnimation },
              legend: { align: 'right', verticalAlign: 'middle', layout: 'vertical', x: -6 },
              series: [
                { center: ['45%', '50%'], size: '80%' },
                { center: ['45%', '50%'], size: '88%' },
                { center: ['45%', '50%'], size: '88%' },
                { center: ['45%', '50%'], size: '88%' }
              ]
            }
          }
        ]
      }
    };

    return { options: optionsMemo, totalCryptography: totalCryptographyLocal };
  }, [
    certs,
    publicKeys,
    privateKeys,
    invalidCerts,
    publicFoundInPrivate,
    privateFoundInPublic,
    handleOnClick,
    theme,
    disableAnimation
  ]);

  return (
    <Box sx={{ maxWidth: 400, width: '100%', margin: '0 auto' }}>
      <div className={classes.mainChart} id="cryptography-box">
        <HighchartsReact
          highcharts={Highcharts}
          constructorType={'chart'}
          options={options}
          immutable={true}
          updateArgs={[true, true]}
          containerProps={{ className: classes.mainChart, style: { width: '100%', maxWidth: '400px' } }}
        />
        <div
          ref={centerTitleRef}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            transform: 'translate(-50%, -50%)',
            width: 83,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'none',
            visibility: isCenterTitleReady ? 'visible' : 'hidden',
            color: theme.palette.text.secondary,
            ...theme.typography.subtitle2
          }}
        >
          <span className="donut-value" style={{ color: theme.palette.text.primary }}>
            {totalCryptography}
          </span>
          <span>Total</span>
        </div>
      </div>
    </Box>
  );
}

export default NRCryptographyChart2;

function adjustSections(begin, end) {
  let minWidthConstant = 3.6;
  let minWidth = INNER_DONUT_MIN_WIDTH_PERCENTAGE * minWidthConstant;
  let sectionWidths = end.map((e, i) => e - begin[i]); // Calculate widths
  let totalCurrentWidth = sectionWidths.reduce((sum, width) => sum + width, 0);
  let totalExtra = 0;

  for (let i = 0; i < sectionWidths.length; i++) {
    if (sectionWidths[i] > 0 && sectionWidths[i] < minWidth) {
      totalExtra += minWidth - sectionWidths[i];
      sectionWidths[i] = minWidth;
    }
  }

  if (totalExtra > 0) {
    let totalRemainingWidth = totalCurrentWidth - totalExtra;

    for (let i = 0; i < sectionWidths.length; i++) {
      if (sectionWidths[i] > minWidth) {
        let proportion = sectionWidths[i] / totalRemainingWidth;
        sectionWidths[i] -= totalExtra * proportion;
      }
    }
  }

  for (let i = 0; i < begin.length; i++) {
    if (i === 0) {
      end[i] = begin[i] + sectionWidths[i];
    } else {
      begin[i] = end[i - 1];
      end[i] = begin[i] + sectionWidths[i];
    }
  }

  end[end.length - 1] = 360;
}

function calculatePercentage(value, valuesArray) {
  const sum = valuesArray.reduce((acc, curr) => acc + curr, 0);
  if (value === 0 || sum === 0) {
    return 0;
  }
  const pct = (value / sum) * 100;
  return pct < INNER_DONUT_MIN_WIDTH_PERCENTAGE ? INNER_DONUT_MIN_WIDTH_PERCENTAGE : pct;
}

function applyMinWidth(percInvalid, minWidth) {
  const val = percInvalid * 100;
  if (val < minWidth) return minWidth;
  if (val > 100 - minWidth) return 100 - minWidth;
  return val;
}
